import { inject, Injectable } from '@angular/core';
import { CanActivate, GuardResult, MaybeAsync, Router } from '@angular/router';
import {
	authState,
	FIREBASE_AUTH,
	ORGANIZATION_ID,
	SplashService,
} from '@context/frontend/common';
import { Subject, takeUntil } from 'rxjs';
import { GuestAuthService } from '../services';

@Injectable({ providedIn: 'root' })
export class GuestAuthGuard implements CanActivate {
	private readonly auth = inject(FIREBASE_AUTH);
	private readonly guestAuthService = inject(GuestAuthService);
	private readonly router = inject(Router);
	private readonly splash = inject(SplashService);
	private readonly organizationId$ = inject(ORGANIZATION_ID);

	/**
	 * Guards routes that require an authenticated guest (companion code) session.
	 * Waits for Firebase auth state; allows access only when signed in via companion code.
	 * Redirects to /auth when not authenticated or when guest session is invalid.
	 * Sessions expire monthly (30 days) and require re-authentication.
	 */
	canActivate(): MaybeAsync<GuardResult> {
		this.splash.onShow();
		const resolved$ = new Subject<boolean>();

		return new Promise<{ allowed: boolean; expired?: boolean }>(
			(resolve) => {
				authState(this.auth)
					.pipe(takeUntil(resolved$))
					.subscribe(async (user) => {
						if (user === null) {
							resolve({ allowed: false });
							resolved$.next(true);
							return;
						}

						if (!this.guestAuthService.hasCode()) {
							resolve({ allowed: false });
							resolved$.next(true);
							return;
						}

						// Prefer orgId from ID token (custom claims) so it's available on reload
						const tokenResult = await user.getIdTokenResult(true);

						const orgId =
							this.organizationId$.value ??
							(tokenResult.claims['orgId'] as string | undefined);
						if (!orgId) {
							resolve({ allowed: false });
							throw Error('Organization not found');
						}

						// Verify guest session exists and has not expired in the organization
						await this.guestAuthService
							.setupGuestSession(user.uid, orgId)
							.then((sessionValid) => {
								resolve({
									allowed: sessionValid,
									expired: !sessionValid,
								});
							})
							.finally(() => {
								resolved$.next(true);
							});
					});
			},
		)
			.catch(() => {
				resolved$.next(true);
				return { allowed: false, expired: false };
			})
			.then((result) => {
				this.splash.onHide();

				if (!result.allowed) {
					return this.guestAuthService
						.signOut()
						.then(() =>
							this.router.navigate(['/auth'], {
								queryParams: result.expired
									? { expired: 'true' }
									: undefined,
							}),
						)
						.then(() => false);
				}
				return true;
			});
	}
}
